import { memo, useCallback, useEffect, useMemo, type SyntheticEvent } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  TextField,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import CategoryIcon from '@material-ui/icons/Category';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import Box from '@mui/material/Box';

import type { ServiceCallTypeOption } from 'types/serviceCalls';

import useTypeOptions from '../hooks/useTypeOptions';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 2000,
    position: 'relative',
  },
  cardHeader: {
    alignItems: 'center',
  },
  avatar: {
    fontSize: 20,
    backgroundColor: theme.palette.primary.main,
  },
}));

const filter = createFilterOptions<ServiceCallTypeOption>({
  matchFrom: 'any',
  stringify: (option) => `${option.displayName ?? ''} ${option.name ?? ''}`,
});

interface TypeCardProps {
  selectType?: (type: ServiceCallTypeOption) => void;
  type?: ServiceCallTypeOption | string | null;
  showJustAutocomplete?: boolean;
  preselectFirst?: boolean;
}

const TypeCard = ({
  selectType,
  type,
  showJustAutocomplete = false,
  preselectFirst = true,
}: TypeCardProps) => {
  const classes = useStyles();
  const { storeType, typeOptionsForServiceCall } = useTypeOptions();
  const isLoading = typeOptionsForServiceCall.isLoading || storeType.isLoading;

  const handleSelectType = useCallback(
    (event: SyntheticEvent, selectedType: ServiceCallTypeOption | null) => {
      if (selectType && selectedType) {
        if (selectedType.isNew) {
          storeType.mutate(selectedType.name, { onSuccess: selectType });
          return;
        }

        selectType(selectedType);
      }
    },
    [selectType, storeType],
  );

  const filterOptions = useCallback(
    (
      opts: ServiceCallTypeOption[],
      state: { inputValue: string },
    ) => {
      const filtered = filter(opts, state as Parameters<typeof filter>[1]);
      const name = state.inputValue.trim();
      const typeExists = opts.some(
        (option) => (option.name ?? '').toLowerCase() === name.toLowerCase(),
      );

      if (name && !typeExists) {
        filtered.push({
          displayName: `Add "${name}"`,
          isNew: true,
          name,
        });
      }

      return filtered;
    },
    [],
  );

  const renderOption = useCallback(
    (option: ServiceCallTypeOption) => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {option.image && (
          <Avatar alt="Image" src={option.image} variant="square" />
        )}
        <Box component="span">
          {option.displayName ?? option.name ?? option.value}
        </Box>
      </Box>
    ),
    [],
  );

  const getOptionLabel = useCallback(
    (option: ServiceCallTypeOption) => option.name ?? option.value ?? '',
    [],
  );

  useEffect(() => {
    const firstType = (typeOptionsForServiceCall.data as ServiceCallTypeOption[] | undefined)?.[0];

    if (preselectFirst && selectType && !type && firstType) {
      selectType(firstType);
    }
  }, [preselectFirst, selectType, type, typeOptionsForServiceCall.data]);

  const dropDown = useMemo(
    () => {
      if (selectType) {
        return (
          <Autocomplete
            renderOption={renderOption}
            filterOptions={filterOptions}
            disableClearable={true}
            disabled={storeType.isLoading}
            filterSelectedOptions={true}
            getOptionLabel={getOptionLabel}
            includeInputInList={true}
            loading={isLoading}
            onChange={handleSelectType}
            options={(typeOptionsForServiceCall?.data as ServiceCallTypeOption[]) || []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Type"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            value={type || null}
          />
        );
      }
      return (
        <TextField
          fullWidth={true}
          label=""
          variant="outlined"
          value={type || ''}
          InputProps={{
            readOnly: true,
          }}
        />
      );
    },
    [
      selectType,
      storeType,
      typeOptionsForServiceCall?.data,
      filterOptions,
      handleSelectType,
      getOptionLabel,
      isLoading,
      renderOption,
      type,
    ],
  );

  if (showJustAutocomplete) {
    return dropDown;
  }

  return (
    <Card className={classes.root}>
      <CardHeader
        className={classes.cardHeader}
        avatar={
          <Avatar aria-label="type" className={classes.avatar}>
            <CategoryIcon />
          </Avatar>
        }
        title={<Typography variant="h3">Type</Typography>}
      />
      <CardContent>{dropDown}</CardContent>
    </Card>
  );
};

export default memo(TypeCard);
